(() => {
  'use strict';

  const sectionsWithin = (root, selector) => [
    ...(root instanceof Element && root.matches(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];

  function initCollections(root = document) {
    sectionsWithin(root, '[data-formi-collection]').forEach((section) => {
      if (section.dataset.formiReady) return;
      section.dataset.formiReady = 'true';
      const sort = section.querySelector('[data-formi-sort]');
      sort?.addEventListener('change', () => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        location.assign(url.toString());
      });
      const submit = section.querySelector('.formi-sort-submit');
      if (submit && sort) submit.hidden = true;
      const filters = section.querySelector('[data-formi-kind-filters]');
      if (filters?.dataset.complete !== 'true') return;
      const items = [...section.querySelectorAll('[data-formi-kind]')];
      const kinds = [...new Set(items.map((item) => item.dataset.formiKind).filter(Boolean))];
      if (kinds.length < 2) return;
      const count = section.querySelector('[data-formi-product-count]');
      const empty = section.querySelector('[data-formi-filter-empty]');
      [null, ...kinds].forEach((kind) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `formi-kind-button${kind === null ? ' is-active' : ''}`;
        button.textContent = kind ?? section.dataset.allLabel;
        button.setAttribute('aria-pressed', String(kind === null));
        button.addEventListener('click', () => {
          let visible = 0;
          items.forEach((item) => {
            item.hidden = kind !== null && item.dataset.formiKind !== kind;
            if (!item.hidden) visible += 1;
          });
          filters.querySelectorAll('button').forEach((other) => {
            const selected = other === button;
            other.classList.toggle('is-active', selected);
            other.setAttribute('aria-pressed', String(selected));
          });
          if (count) count.textContent = (section.dataset.countTemplate || '__COUNT__').replace('__COUNT__', visible);
          if (empty) empty.hidden = visible !== 0;
        });
        filters.append(button);
      });
      filters.hidden = false;
    });
  }

  function initProducts(root = document) {
    sectionsWithin(root, '[data-formi-product]').forEach((section) => {
      if (section.dataset.formiReady) return;
      section.dataset.formiReady = 'true';
      const select = section.querySelector('[data-variant-select]');
      const picker = section.querySelector('[data-formi-option-picker]');
      const fallback = section.querySelector('[data-formi-variant-control]');
      const groups = [...section.querySelectorAll('[data-formi-option-position]')];
      const price = section.querySelector('[data-variant-price]');
      const quantity = section.querySelector('[name="quantity"]');
      const add = section.querySelector('[data-add-button]');
      const status = section.querySelector('[data-formi-variant-status]');
      const image = section.querySelector('#gallery-image');
      const form = section.querySelector('.product-form');
      if (!select || !quantity || !add || !form) return;
      const variants = [...select.options].filter((option) => option.value).map((option) => {
        try { return { option, values: JSON.parse(option.dataset.options) }; }
        catch (_) { return { option, values: [] }; }
      });
      const highVariants = Number(section.dataset.variantsCount) > variants.length;
      let selections = selectedValues();
      let requestController;

      function selectedValues() {
        const current = select.selectedOptions[0];
        try { return JSON.parse(current?.dataset.options || '[]'); }
        catch (_) { return []; }
      }

      function setImage(source, srcset, alt) {
        if (!image || !source) return;
        image.src = source;
        if (srcset) image.srcset = srcset;
        else image.removeAttribute('srcset');
        if (alt) image.alt = alt;
        section.querySelectorAll('.gallery-thumb').forEach((thumb) => thumb.setAttribute('aria-pressed', String(thumb.dataset.image === source)));
      }

      function syncPicker() {
        groups.forEach((group, index) => {
          const label = group.querySelector('[data-formi-option-label]');
          if (label) label.textContent = selections[index] || '';
          group.querySelectorAll('[data-formi-option-value]').forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.formiOptionValue === selections[index]));
            if (!highVariants) {
              const candidate = selections.map((value, position) => position === index ? button.dataset.formiOptionValue : value);
              const matching = variants.find((entry) => entry.values.length === candidate.length && entry.values.every((value, position) => value === candidate[position]));
              button.dataset.unavailable = String(!matching || matching.option.dataset.available !== 'true');
            }
          });
        });
      }

      function syncQuantity() {
        const minimum = Number(quantity.min) || 1;
        const increment = Number(quantity.step) || 1;
        const maximum = quantity.hasAttribute('max') ? Number(quantity.max) : Infinity;
        let value = Math.max(minimum, Number(quantity.value) || minimum);
        value = minimum + Math.floor((value - minimum) / increment) * increment;
        if (Number.isFinite(maximum)) value = Math.min(value, minimum + Math.floor((maximum - minimum) / increment) * increment);
        quantity.value = String(value);
        section.querySelectorAll('[data-formi-quantity]').forEach((button) => {
          button.disabled = button.dataset.formiQuantity === 'minus' ? value <= minimum : value + increment > maximum;
        });
      }

      function updateVariant(option, updateUrl = true) {
        if (!option || !option.value) {
          select.value = '';
          select.disabled = true;
          add.disabled = true;
          add.textContent = section.dataset.sold;
          if (price) price.hidden = true;
          if (status) status.textContent = section.dataset.unavailable;
          return;
        }
        select.disabled = false;
        select.value = option.value;
        const available = option.dataset.available === 'true';
        add.disabled = !available;
        add.textContent = available ? section.dataset.add : section.dataset.sold;
        if (status) status.textContent = available ? '' : section.dataset.sold;
        if (price) {
          price.hidden = false;
          const sale = option.dataset.onSale === 'true';
          const current = price.querySelector('[data-current-price]');
          const compare = price.querySelector('[data-compare-price]');
          const amount = price.querySelector('[data-compare-amount]');
          const label = price.querySelector('[data-price-label]');
          const badge = price.querySelector('[data-sale-badge]');
          if (current) current.textContent = option.dataset.price;
          if (compare) compare.hidden = !sale;
          if (amount) amount.textContent = sale ? option.dataset.comparePrice : '';
          if (label) label.textContent = sale ? price.dataset.saleLabel : price.dataset.regularLabel;
          if (badge) badge.hidden = !sale;
          price.classList.toggle('price--on-sale', sale);
        }
        quantity.min = option.dataset.min || '1';
        quantity.step = option.dataset.step || '1';
        if (option.dataset.max) quantity.max = option.dataset.max;
        else quantity.removeAttribute('max');
        syncQuantity();
        setImage(option.dataset.image, option.dataset.imageSrcset, option.dataset.imageAlt);
        if (updateUrl) {
          const url = new URL(location.href);
          url.searchParams.set('variant', option.value);
          url.searchParams.delete('option_values');
          history.replaceState({}, '', url);
        }
      }

      async function loadSelectedOptions(clickedButton) {
        const optionIds = groups.map((group, index) => [...group.querySelectorAll('[data-formi-option-value]')].find((button) => button.dataset.formiOptionValue === selections[index])?.dataset.optionValueId);
        if (optionIds.some((id) => !id)) return;
        requestController?.abort();
        requestController = new AbortController();
        const controller = requestController;
        section.setAttribute('aria-busy', 'true');
        add.disabled = true;
        if (status) status.textContent = section.dataset.selecting;
        const url = new URL(clickedButton.dataset.productUrl || section.dataset.productUrl, location.origin);
        url.searchParams.set('section_id', section.dataset.sectionId);
        url.searchParams.set('option_values', optionIds.join(','));
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Product section unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = parsed.querySelector('[data-formi-product]');
          if (!replacement) throw new Error('Product section missing');
          const focusedValue = clickedButton.dataset.formiOptionValue;
          const focusedPosition = clickedButton.closest('[data-formi-option-position]')?.dataset.formiOptionPosition;
          section.replaceWith(replacement);
          initProducts(replacement);
          const selected = replacement.querySelector('[data-variant-select]')?.selectedOptions[0];
          const pageUrl = new URL(clickedButton.dataset.productUrl || section.dataset.productUrl, location.origin);
          if (selected?.value) pageUrl.searchParams.set('variant', selected.value);
          else pageUrl.searchParams.set('option_values', optionIds.join(','));
          history.replaceState({}, '', pageUrl);
          [...replacement.querySelectorAll('[data-formi-option-value]')].find((button) => button.dataset.formiOptionValue === focusedValue && button.closest('[data-formi-option-position]')?.dataset.formiOptionPosition === focusedPosition)?.focus({ preventScroll: true });
        } catch (error) {
          if (error.name === 'AbortError') return;
          if (picker) picker.hidden = true;
          if (fallback) fallback.hidden = false;
          select.disabled = false;
          updateVariant(select.selectedOptions[0], false);
          if (status) status.textContent = section.dataset.selectionError;
          select.focus({ preventScroll: true });
        } finally {
          if (requestController === controller) section.removeAttribute('aria-busy');
        }
      }

      section.querySelectorAll('.gallery-thumb').forEach((button) => button.addEventListener('click', () => setImage(button.dataset.image, button.dataset.imageSrcset, button.dataset.imageAlt)));
      select.addEventListener('change', () => {
        selections = selectedValues();
        updateVariant(select.selectedOptions[0]);
        syncPicker();
      });
      groups.forEach((group, index) => {
        group.querySelectorAll('[data-formi-option-value]').forEach((button) => button.addEventListener('click', () => {
          selections[index] = button.dataset.formiOptionValue;
          syncPicker();
          if (highVariants || button.dataset.productUrl && new URL(button.dataset.productUrl, location.origin).pathname !== new URL(section.dataset.productUrl, location.origin).pathname) {
            loadSelectedOptions(button);
            return;
          }
          const match = variants.find((entry) => entry.values.length === selections.length && entry.values.every((value, position) => value === selections[position]));
          updateVariant(match?.option);
        }));
      });
      section.querySelectorAll('[data-formi-quantity]').forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => {
          quantity.value = String((Number(quantity.value) || Number(quantity.min) || 1) + (button.dataset.formiQuantity === 'minus' ? -1 : 1) * (Number(quantity.step) || 1));
          syncQuantity();
        });
      });
      quantity.addEventListener('change', syncQuantity);
      form.addEventListener('submit', (event) => {
        const current = select.selectedOptions[0];
        if (select.disabled || !current?.value || current.dataset.available !== 'true') {
          event.preventDefault();
          if (status) status.textContent = section.dataset.unavailable;
        }
      });
      if (groups.length) {
        if (!selections.length) selections = groups.map((group) => group.querySelector('[aria-pressed="true"]')?.dataset.formiOptionValue || '');
        if (picker) picker.hidden = false;
        if (fallback) fallback.hidden = true;
        syncPicker();
      }
      updateVariant(select.selectedOptions[0], false);
      syncQuantity();
    });
  }

  initCollections();
  initProducts();
  document.addEventListener('shopify:section:load', (event) => {
    initCollections(event.target);
    initProducts(event.target);
  });
})();
